/**
 * Client-side audio watermark. v3
 *
 * A near-ultrasonic tone is switched between two frequencies (binary FSK,
 * 10 bits/s) to repeat one 56-bit frame forever:
 *     SYNC (8 bits) + session code (32) + CRC-16 (16)
 * It carries the SAME 32-bit code the video watermark carries (v2 sent bytes
 * of the HMAC, which the server never stored, so it could not be looked up).
 *
 * Honest limits: survives lossless / high-bitrate copies and room-less
 * system-audio capture. It does NOT survive lossy encoders that low-pass
 * below ~18 kHz (most 128 kbps AAC/MP3). Needs ~8 s of audio to decode.
 *
 * KEEP CONSTANTS IDENTICAL IN server/utils/rightra/watermarkExtractor.js
 */
import { packPayload } from "./watermark-video.js";

export const F0 = 17500;
export const F1 = 18300;
export const BIT_SECONDS = 0.1;
export const SYNC = [1, 0, 1, 1, 0, 0, 1, 0];
const LEVEL = 0.002;

export function buildAudioBits(code) {
  return [...SYNC, ...packPayload(code >>> 0)];
}

export class AudioWatermark {
  /** @param {number} code 32-bit session watermark code */
  constructor(code) {
    this.bits = buildAudioBits(code);
    this.ctx = null;
    this.source = null;
    this.osc = null;
    this.gain = null;
    this.video = null;
    this.running = false;
    this._timer = null;
    this._t = 0;
    this._i = 0;
    this._resume = null;
  }

  attach(videoElement) {
    if (this.running || !videoElement) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) throw new Error("Web Audio is not available.");
    try {
      this.ctx = new Ctx();
      this.video = videoElement;
      this._resume = () => {
        if (this.ctx && this.ctx.state === "suspended") this.ctx.resume().catch(() => {});
      };
      videoElement.addEventListener("play", this._resume);
      videoElement.addEventListener("playing", this._resume);

      this.source = this.ctx.createMediaElementSource(videoElement);
      this.source.connect(this.ctx.destination);

      this.osc = this.ctx.createOscillator();
      this.osc.type = "sine";
      this.osc.frequency.value = F0;
      this.gain = this.ctx.createGain();
      this.gain.gain.value = LEVEL;
      this.osc.connect(this.gain);
      this.gain.connect(this.ctx.destination);
      this.osc.start();

      this._t = this.ctx.currentTime;
      this._i = 0;
      this._schedule();
      this._timer = setInterval(() => this._schedule(), 1000);
      this._resume();
      this.running = true;
    } catch (e) {
      this.detach();
      throw e;
    }
  }

  _schedule() {
    if (!this.ctx || !this.osc) return;
    const now = this.ctx.currentTime;
    if (this._t < now) this._t = now;
    while (this._t < now + 3) {
      const bit = this.bits[this._i % this.bits.length];
      this.osc.frequency.setValueAtTime(bit ? F1 : F0, this._t);
      this._t += BIT_SECONDS;
      this._i++;
    }
  }

  detach() {
    clearInterval(this._timer);
    this._timer = null;
    if (this.video && this._resume) {
      this.video.removeEventListener("play", this._resume);
      this.video.removeEventListener("playing", this._resume);
    }
    try { this.osc?.stop(); } catch { /* already stopped */ }
    try {
      this.source?.disconnect();
      this.gain?.disconnect();
      this.osc?.disconnect();
    } catch { /* already disconnected */ }
    try { this.ctx?.close(); } catch { /* already closed */ }
    this.ctx = this.source = this.osc = this.gain = this.video = null;
    this.running = false;
  }
}